import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Megaphone, Facebook, Instagram, Linkedin, MessageSquare, Star,
  PenSquare, Bell, Settings, RefreshCw, Plus, ChevronRight,
  Eye, ThumbsUp, Share2, Clock, CheckCircle, AlertTriangle, Bot
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

// Google icon component
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function MarketingDashboard() {
  const { user, isAssistant } = useOutletContext();
  const [dashboard, setDashboard] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('client_token');
    setLoading(true);

    try {
      const [dashboardRes, accountsRes, agentsRes] = await Promise.all([
        fetch(`${API_URL}/api/marketing/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/marketing/accounts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/marketing/agents`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (accountsRes.ok) setAccounts(await accountsRes.json());
      if (agentsRes.ok) setAgents(await agentsRes.json());
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook': return <Facebook className="w-5 h-5" />;
      case 'instagram': return <Instagram className="w-5 h-5" />;
      case 'linkedin': return <Linkedin className="w-5 h-5" />;
      case 'google_business': return <GoogleIcon className="w-5 h-5" />;
      default: return <Megaphone className="w-5 h-5" />;
    }
  };

  const getPlatformColor = (platform) => {
    switch (platform) {
      case 'facebook': return 'bg-blue-500';
      case 'instagram': return 'bg-gradient-to-tr from-purple-500 via-pink-500 to-orange-500';
      case 'linkedin': return 'bg-blue-700';
      case 'google_business': return 'bg-white border border-slate-200';
      default: return 'bg-slate-500';
    }
  };

  const getAgentName = (type) => {
    switch (type) {
      case 'community_manager': return 'Community Manager';
      case 'content_creator': return 'Content Creator';
      case 'reputation_manager': return 'Reputation Manager';
      default: return type;
    }
  };

  const getAgentDescription = (type) => {
    switch (type) {
      case 'community_manager': return 'Répond aux commentaires et messages';
      case 'content_creator': return 'Génère et publie du contenu';
      case 'reputation_manager': return 'Répond aux avis Google';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="w-6 h-6 text-rm-primary" />
          <div>
            <h1 className="text-xl font-bold text-rm-text">Marketing</h1>
            <p className="text-sm text-slate-500">Gère tes réseaux sociaux et ta réputation en ligne</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <Megaphone className="w-4 h-4" />
            <span className="text-xs">Comptes connectés</span>
          </div>
          <p className="text-2xl font-bold text-rm-text">{dashboard?.connected_accounts || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <Clock className="w-4 h-4" />
            <span className="text-xs">À approuver</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{dashboard?.pending_approval || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <MessageSquare className="w-4 h-4" />
            <span className="text-xs">Commentaires</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{dashboard?.unread_comments || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <Bell className="w-4 h-4" />
            <span className="text-xs">Messages</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{dashboard?.unread_messages || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-2">
            <Star className="w-4 h-4" />
            <span className="text-xs">Avis Google</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{dashboard?.unread_reviews || 0}</p>
        </div>
      </div>

      {/* Connected Accounts */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-rm-text flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-rm-primary" />
            Comptes connectés
          </h2>
          <Link to="/espace-client/marketing/accounts">
            <Button size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Connecter
            </Button>
          </Link>
        </div>
        
        {accounts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
            {accounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${getPlatformColor(account.platform)}`}>
                  {getPlatformIcon(account.platform)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{account.platform_name}</p>
                  <p className="text-xs text-slate-500 capitalize">{account.platform.replace('_', ' ')}</p>
                </div>
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Megaphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 mb-4">Aucun compte connecté</p>
            <Link to="/espace-client/marketing/accounts">
              <Button>Connecter mes réseaux</Button>
            </Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Link to="/espace-client/marketing/posts" className="block">
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-rm-primary hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <PenSquare className="w-5 h-5 text-blue-600" />
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-rm-primary transition-colors" />
            </div>
            <h3 className="font-semibold text-rm-text">Publications</h3>
            <p className="text-xs text-slate-500 mt-1">Créer et planifier des posts</p>
          </div>
        </Link>

        <Link to="/espace-client/marketing/comments" className="block">
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-rm-primary hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex items-center gap-2">
                {(dashboard?.unread_comments || 0) > 0 && (
                  <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {dashboard.unread_comments}
                  </span>
                )}
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-rm-primary transition-colors" />
              </div>
            </div>
            <h3 className="font-semibold text-rm-text">Commentaires</h3>
            <p className="text-xs text-slate-500 mt-1">Répondre aux interactions</p>
          </div>
        </Link>

        <Link to="/espace-client/marketing/messages" className="block">
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-rm-primary hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Bell className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex items-center gap-2">
                {(dashboard?.unread_messages || 0) > 0 && (
                  <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {dashboard.unread_messages}
                  </span>
                )}
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-rm-primary transition-colors" />
              </div>
            </div>
            <h3 className="font-semibold text-rm-text">Messages privés</h3>
            <p className="text-xs text-slate-500 mt-1">Facebook & Instagram</p>
          </div>
        </Link>

        <Link to="/espace-client/marketing/reviews" className="block">
          <div className="bg-white rounded-xl border border-slate-200 p-4 hover:border-rm-primary hover:shadow-md transition-all cursor-pointer group">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex items-center gap-2">
                {(dashboard?.unread_reviews || 0) > 0 && (
                  <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {dashboard.unread_reviews}
                  </span>
                )}
                <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-rm-primary transition-colors" />
              </div>
            </div>
            <h3 className="font-semibold text-rm-text">Avis Google</h3>
            <p className="text-xs text-slate-500 mt-1">Gérer ton e-réputation</p>
          </div>
        </Link>
      </div>

      {/* AI Agents */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-rm-text flex items-center gap-2">
            <Bot className="w-5 h-5 text-rm-primary" />
            Agents IA
          </h2>
          <Link to="/espace-client/marketing/agents">
            <Button variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-2" />
              Configurer
            </Button>
          </Link>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
          {agents.map((agent) => (
            <div
              key={agent.agent_type}
              className={`p-4 rounded-lg border ${agent.is_active ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Bot className={`w-5 h-5 ${agent.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                  <span className="font-medium text-sm">{getAgentName(agent.agent_type)}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  agent.is_active 
                    ? 'bg-green-200 text-green-700' 
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {agent.is_active ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <p className="text-xs text-slate-500">{getAgentDescription(agent.agent_type)}</p>
              {agent.is_active && (
                <p className="text-xs text-slate-400 mt-2">
                  Mode: {agent.validation_mode === 'supervised' ? 'Supervisé' : agent.validation_mode === 'semi_auto' ? 'Semi-auto' : 'Auto'}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-rm-primary/10 to-rm-secondary/10 rounded-xl p-6 border border-rm-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-rm-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Bot className="w-6 h-6 text-rm-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-rm-text mb-2">Gagnez du temps avec les Agents IA</h3>
            <p className="text-sm text-slate-600">
              Configure tes agents pour qu'ils répondent automatiquement aux commentaires simples, 
              génèrent du contenu adapté à ton activité, et gèrent tes avis Google. 
              Vous gardez le contrôle avec le mode supervisé ou semi-automatique.
            </p>
            <Link to="/espace-client/marketing/agents" className="inline-block mt-3">
              <Button size="sm" variant="outline">
                Configurer les agents
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
